import React, { useContext, useEffect } from 'react';
import queryString from 'query-string-for-all';
import { useDispatch } from 'react-redux';
import { v4 as uuidV4 } from 'uuid';
import { AppContext } from '@prisjakt/ui-app-context';
import {
  filtersToQueryObject,
  getStringQuery,
} from '@prisjakt/product-collection-urls';
import { PageContent } from 'src/components/page';
import {
  ProductCollection as ProductCollectionType,
  PrismicArticle,
  ProductCollectionSlice as ProductCollectionSliceType,
  FeaturedArticlesSlice as FeaturedArticlesSliceType,
  AdvizedToplist,
  ExpertCategoryContent,
} from '../../types';
import ProductCollectionSlice from './components/slice';
import WordCloud from '../../components/word-cloud';
import RouteContext from '../../routes/context';
import { ProductCollectionTrackingContext } from './filter-tracking';

import { compareByDate } from 'src/areas/expert/utils';
import { useHistory } from 'react-router';

type Props = {
  data: {
    productCollection: ProductCollectionType;
  };
};

const getFeaturedArticlesSlice = (
  editorialArticles: PrismicArticle[],
  expert?: ExpertCategoryContent,
  topListsOffset: number = 2 // We slice 2 articles for the page, rest go into the AdvizedCarousel
): FeaturedArticlesSliceType | undefined => {
  let toplistArticles: AdvizedToplist[] = [];
  const featuredArticle: PrismicArticle | undefined = editorialArticles.find(
    (a: PrismicArticle) => {
      return a.data.body.find(
        b =>
          b.primary.__typename === 'PrismicSliceProductList' &&
          b.primary.products!.length > 0
      );
    }
  );
  if (expert && expert!.toplists) {
    toplistArticles = expert!.toplists
      .sort(compareByDate)
      .slice(0, topListsOffset) as AdvizedToplist[];
  }
  return {
    __typename: 'FeaturedArticlesSlice',
    id: uuidV4(),
    featuredArticle,
    toplistArticles,
    expert,
  };
};

const insertFeaturedArticleSlice = (
  slices: ProductCollectionSliceType[],
  featuredArticleSlice: FeaturedArticlesSliceType
) => {
  return slices.reduce((acc, slice) => {
    if (slice.__typename === 'DescriptionSlice') {
      // eslint-disable-next-line no-param-reassign
      acc = [...acc, slice, featuredArticleSlice];
      return acc;
    }
    return [...acc, slice];
  }, [] as ProductCollectionSliceType[]);
};

const getProductCollectionPathKey = (state: any) => {
  const filters = (state?.filters || []).map((filter: any) =>
    filter.type === 'range'
      ? {
          // Don't update pathKey for different ranges
          ...filter,
          from: '',
          to: '',
        }
      : filter
  );
  return `${getStringQuery(filtersToQueryObject(filters))}:${state?.query
    ?.offset || 0}`;
};

const ProductCollection = ({ data }: Props) => {
  const { t, linkTo } = useContext(AppContext);
  const { getState } = useContext(RouteContext);
  const dispatch = useDispatch();
  const history = useHistory();

  const currentState = getState();
  const { productCollection } = data;
  const { popularSearchTerms, slices } = productCollection || {};
  const pathKey = getProductCollectionPathKey(currentState);
  let finalSlices: ProductCollectionSliceType[] = [...slices];

  if (productCollection.editorialArticles) {
    const featuredArticleSlice = getFeaturedArticlesSlice(
      productCollection.editorialArticles,
      productCollection.expert!
    );

    if (featuredArticleSlice) {
      finalSlices = insertFeaturedArticleSlice(
        finalSlices,
        featuredArticleSlice
      );
    }
  }

  useEffect(() => {
    dispatch({
      type: 'ADS_PATHKEY_UPDATE',
      pathKey,
    });
  }, [dispatch, pathKey]);

  const fullUrl = React.useMemo(() => {
    if (!productCollection?.url) return null;

    const { offset, sort, direction } = currentState;
    const { url, query } = queryString.parseUrl(productCollection.url);
    if (offset > 0) {
      query.offset = offset;
    }
    if (sort) {
      query.sort = sort;
    }
    if (direction) {
      query.direction = direction;
    }

    const { query: locationQuery } = queryString.parseUrl(
      `${history.location.pathname}${history.location.search}`
    );

    if (locationQuery.ast_override_inv_code) {
      query.ast_override_inv_code = locationQuery.ast_override_inv_code;
    }

    const queryStr = Object.keys(query).length
      ? `?${queryString.stringify(query)}`
      : '';

    return `${url}${queryStr}`;
  }, [currentState, history, productCollection]);

  useEffect(() => {
    const oldUrl = `${history.location.pathname}${history.location.search}`;
    if (fullUrl != null && oldUrl !== fullUrl) {
      history.push(fullUrl);
    }
  }, [history, fullUrl]);

  return (
    <ProductCollectionTrackingContext.Provider
      value={{
        categoryId: data.productCollection.slug,
        categoryName: data.productCollection.name,
      }}
    >
      <PageContent data-test="ProductCollectionPage" withAds>
        {finalSlices?.map(
          slice =>
            slice && (
              <ProductCollectionSlice
                key={`${slice.id}_${slice.__typename}`}
                slice={slice}
                productCollection={productCollection}
              />
            )
        )}
        {Array.isArray(popularSearchTerms) && popularSearchTerms.length ? (
          <WordCloud
            title={t('search', 'search.related-searches')}
            items={popularSearchTerms.slice(0, 100)}
            showMoreUrl={linkTo('keywordListing', productCollection)}
          />
        ) : null}
      </PageContent>
    </ProductCollectionTrackingContext.Provider>
  );
};

export default ProductCollection;
